import React, { useState, useEffect, useRef } from 'react';
import { InputNumber, message } from 'antd';

const ValidatedInputNumber = ({
    value,
    onChange,
    form,
    fieldName,
    min = 0,
    max = null,
    precision = 2,
    step = 0.01,
    keyboard = false,
    disabled = false,
    placeholder = "0.00",
    style = {},
    formatter = null,
    parser = null,
    validationRules = {},
    correctionMessages = {},
    className = "",
    ...otherProps
}) => {
    const [correctionIndicator, setCorrectionIndicator] = useState(false);
    const [internalValue, setInternalValue] = useState(value);
    const [isEditing, setIsEditing] = useState(false);
    const inputRef = useRef(null);
    const prevValueRef = useRef(value);

    // Sync internal value with prop value
    useEffect(() => {
        // STRICT RULE: While editing (focused), never sync from parent
        // This preserves user's exact input while typing
        // On blur, formatting happens and isEditing becomes false, allowing sync
        if (!isEditing) {
            setInternalValue(value);
            prevValueRef.current = value;
        }
    }, [value, isEditing]);

    // Default validation rules
    const defaultRules = {
        allowNegative: false,
        enforceMax: max !== null,
        enforceMin: true
    };

    // Merge with custom rules
    const rules = { ...defaultRules, ...validationRules };

    // Default correction messages
    const defaultMessages = {
        negative: 'Amount corrected: Cannot be negative',
        exceedsMax: `Amount corrected: Maximum ${max}`,
        belowMin: `Amount corrected: Minimum ${min}`
    };

    // Merge with custom messages
    const messages = { ...defaultMessages, ...correctionMessages };

    const showCorrectionIndicator = () => {
        setCorrectionIndicator(true);
        setTimeout(() => {
            setCorrectionIndicator(false);
        }, 2000);
    };

    const handleChange = (inputValue) => {
        // Mark as editing when user types
        setIsEditing(true);

        // Update internal value immediately
        setInternalValue(inputValue);

        // Call parent onChange immediately for real-time updates (variance calculation)
        // The isEditing flag will prevent the value from syncing back and overwriting user input
        if (onChange) {
            onChange(inputValue);
        }
    };

    const selectNumericPortion = (input) => {
        // Helper function to select only the numeric portion (exclude currency code)
        if (input && formatter) {
            const value = input.value;
            // Find where numbers start (after currency code like "USD ", "SGD ", etc.)
            const match = value.match(/[\d.,]/);
            if (match) {
                const numericStart = match.index;
                input.setSelectionRange(numericStart, value.length);
            } else {
                // Fallback to select all if no numbers found
                input.select();
            }
        } else {
            // If no formatter, select all text
            input?.select();
        }
    };

    const handleFocus = (e) => {
        // Mark as editing
        setIsEditing(true);

        // Note: Auto-selection disabled to prevent backspace issues
        // Users can manually select text if needed
        // The selectNumericPortion logic was causing issues where
        // backspace would trigger unexpected behavior on selected text
    };

    const handleClick = (e) => {
        // When user clicks, select only the numeric portion (exclude currency code)
        const input = inputRef.current?.querySelector('input');
        if (input && document.activeElement === input) {
            // Only apply if input is already focused (to not interfere with initial focus)
            setTimeout(() => {
                selectNumericPortion(input);
            }, 0);
        }
    };

    const handleDoubleClick = (e) => {
        // When user double-clicks, select only the numeric portion (exclude currency code)
        const input = inputRef.current?.querySelector('input');
        if (input) {
            // Use setTimeout to ensure this runs after browser's default double-click selection
            setTimeout(() => {
                selectNumericPortion(input);
            }, 0);
        }
    };

    const handleBlur = (e) => {
        let finalValue = internalValue;

        // Format the value on blur (round to precision)
        if (internalValue !== null && internalValue !== undefined && internalValue !== '') {
            let numValue = parseFloat(internalValue);

            if (!isNaN(numValue)) {
                // Apply validation rules
                let correctionMessage = '';

                // Check for negative values
                if (!rules.allowNegative && numValue < min) {
                    numValue = min;
                    correctionMessage = messages.belowMin || messages.negative;
                }
                // Check for values exceeding maximum
                else if (rules.enforceMax && max !== null && numValue > max) {
                    numValue = parseFloat(max.toFixed(precision));
                    correctionMessage = messages.exceedsMax;
                }

                if (correctionMessage) {
                    message.warning(correctionMessage);
                    showCorrectionIndicator();
                }

                // Format to precision - round to avoid floating-point artifacts
                const multiplier = Math.pow(10, precision);
                finalValue = Math.round(numValue * multiplier) / multiplier;

                // Mark as done editing FIRST - this allows formatter to be triggered
                setIsEditing(false);

                // Then set value (this will trigger formatter since isEditing is now false)
                setInternalValue(finalValue);

                // Update parent with formatted value (if different from what was typed)
                if (onChange && finalValue !== internalValue) {
                    onChange(finalValue);
                }
            }
        } else {
            // Mark as done editing even if no value
            setIsEditing(false);
        }
    };

    // Smart formatter that only shows decimals when needed
    const defaultFormatter = (value) => {
        if (!value) return '';
        return value.toString();
    };

    // Wrapper formatter: bypass formatting while editing (clean input), format when blurred (add commas)
    const wrappedFormatter = formatter ? (value) => {
        if (isEditing) {
            // While editing, show raw number without formatting
            return value != null ? value.toString() : '';
        }
        // Not editing (blurred), apply formatter with commas
        return formatter(value);
    } : defaultFormatter;

    return (
        <div ref={inputRef} style={{ width: '100%' }} onClick={handleClick} onDoubleClick={handleDoubleClick}>
            <InputNumber
                {...otherProps}
                value={internalValue}
                onChange={handleChange}
                onFocus={handleFocus}
                onBlur={handleBlur}
                precision={precision}
                step={step}
                min={rules.enforceMin ? min : undefined}
                disabled={disabled}
                placeholder={placeholder}
                style={{
                    transition: 'all 0.3s ease',
                    ...(correctionIndicator ? {
                        borderColor: '#ff6b35',
                        backgroundColor: '#fff2e8'
                    } : {}),
                    ...style
                }}
                className={`validated-input-number ${className}`}
                formatter={wrappedFormatter}
                parser={parser}
                controls={false}
                keyboard={keyboard}
            />
        </div>
    );
};

export default ValidatedInputNumber;